// IndexedDB persister for the React-Query cache. Lets thread lists and
// thread bodies survive a hard refresh — and lets the background prefetch
// sweep stash up to 100 threads/account so users see instant opens.
//
// We deliberately use IndexedDB (via idb-keyval) instead of localStorage:
// 100 threads × N inboxes × full HTML bodies easily blow past localStorage's
// 5–10 MB cap, and IDB is async so it doesn't block the main thread.
//
// SECURITY NOTE: cached bodies are decrypted plaintext in IDB. Anyone with
// access to this browser profile can read those emails. Same model as
// Gmail's own offline mode — acceptable for V1.

import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { get, set, del } from 'idb-keyval';
import type { QueryClient } from '@tanstack/react-query';

// Bumped to v2 to evict the fat v1 caches (rehydrating multi-MB thread
// bodies + inlined data: images was OOMing iOS Safari).
const KEY = 'wharf:query-cache:v3';

/**
 * idb-keyval-backed storage shim matching the AsyncStorage interface
 * react-query-persist-client expects. (`getItem` must return string|null.)
 */
const idbStorage = {
  getItem: async (key: string): Promise<string | null> => {
    const v = await get<string>(key);
    return v ?? null;
  },
  setItem: async (key: string, value: string): Promise<void> => {
    await set(key, value);
  },
  removeItem: async (key: string): Promise<void> => {
    await del(key);
  },
};

export const persister = createAsyncStoragePersister({
  storage: idbStorage,
  key: KEY,
  // Throttle writes — the prefetch sweep can fire many setQueryData calls
  // in a burst and we don't need to flush each one individually.
  throttleTime: 2000,
});

/**
 * Decide whether a given query should be persisted. We deliberately
 * skip 'thread' (the full message bodies — multi-MB inlined data: images
 * blow iOS Safari's tab memory budget on rehydrate). Lists, accounts,
 * and me are tiny + worth re-hydrating cold.
 */
export function shouldPersistQuery(query: { queryKey: readonly unknown[] }): boolean {
  const [head, , , search] = query.queryKey as [string, unknown, unknown, unknown];
  if (head === 'threads') {
    // Search results are session-only; everything else (account_id, folder,
    // empty query) is worth persisting.
    return !search || search === '';
  }
  if (head === 'accounts') return true;
  if (head === 'me') return true;
  // 'thread' detail intentionally NOT persisted any more.
  return false;
}

/** Wipe the persisted cache (call from logout). */
export async function clearPersistedCache(qc: QueryClient): Promise<void> {
  qc.clear();
  await del(KEY);
}
